import { Call02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { useVoiceEnabled } from "providers/Auth";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx, formatPhone } from "shared/utils";
import { useSoftphone } from "softphone";
import { getErrorMessage } from "utils/error";
import { formatCalendar } from "../helpers/format-calendar";
import { Notification } from "../types";

type MissedCallMetadata = Notification["metadata"];

// The backend builds the same link in Notification#url; this is the local
// fallback for a row that arrived with `url: null` (an older API, or a call
// that lost its conversation). `<Link to={undefined}>` misbehaves in
// react-router, so the card must never hand it an empty destination.
// Keep in sync with the backend builder.
const buildMissedCallUrl = (metadata: MissedCallMetadata): string => {
  if (metadata.conversation_id) return `/inbox?c=${metadata.conversation_id}`;
  if (metadata.lead_id) return `/leads/${metadata.lead_id}`;
  return "/calls";
};

// The caller. `from_name` is whatever the CRM could resolve (lead/contact);
// otherwise the number itself is the only identity we have.
const callerLabel = (metadata: MissedCallMetadata): string => {
  const name = metadata.from_name?.trim();
  if (name) return name;
  if (metadata.from_number) return formatPhone(metadata.from_number);
  return "Unknown caller";
};

// "Missed call at {number name}" — the partner-side line that was dialled,
// labelled with the property/provisioned-number name when the backend resolved
// one.
const missedCallTitle = (metadata: MissedCallMetadata): string => {
  const dialled =
    metadata.to_name?.trim() ||
    (metadata.to_number ? formatPhone(metadata.to_number) : "");
  return dialled ? `Missed call at ${dialled}` : "Missed call";
};

/**
 * Dials the caller back. Deliberately rendered OUTSIDE the row's <Link>: an
 * interactive element inside an anchor would place the call *and* navigate
 * away (marking the notification read) on a single click.
 *
 * Seeing the call log only needs the partner feature, but placing a call also
 * needs the per-user permission — hence the extra `useVoiceEnabled()` gate,
 * so the affordance disappears for inbound-only users.
 */
const CallBackButton = ({ metadata }: { metadata: MissedCallMetadata }) => {
  const voiceEnabled = useVoiceEnabled();
  const { placeCall, isReady } = useSoftphone();

  const toNumber = metadata.from_number;
  if (!voiceEnabled || !toNumber) return null;

  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      HugeIcon={Call02Icon}
      disabled={!isReady}
      tooltip={isReady ? undefined : "The softphone is not connected yet"}
      onClick={() =>
        placeCall({
          toNumber,
          leadId: metadata.lead_id ?? undefined,
          contactId: metadata.contact_id ?? undefined,
          customerId: metadata.customer_id ?? undefined
        }).catch((error) => toast.error(getErrorMessage(error)))
      }
    >
      Call back
    </Button>
  );
};

interface MissedCallNotificationCardProps {
  item: Notification;
}

export const MissedCallNotificationCard = ({
  item
}: MissedCallNotificationCardProps) => {
  const queryClient = useQueryClient();
  const { id, read_at, url, metadata, created_at } = item;

  const { mutate: markAsRead } = useMutation({
    mutationKey: ["NOTIFICATIONS", "MARK_AS_READ"],
    mutationFn: (ids: number[]) =>
      instance.post("/portal/v1/notifications/mark_as_read", { ids }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["NOTIFICATIONS"] });
      queryClient.invalidateQueries({
        queryKey: ["UNREAD_NOTIFICATIONS_COUNT"]
      });
      queryClient.invalidateQueries({
        queryKey: ["TOTAL_UNREAD_NOTIFICATIONS"]
      });
      queryClient.invalidateQueries({ queryKey: ["NEWEST_NOTIFICATIONS"] });
    }
  });

  const handleClick = () => {
    if (!read_at) markAsRead([id]);
  };

  const callerName = callerLabel(metadata);
  // `occurred_at` is the terminalization instant the backend also uses as the
  // row's created_at; prefer it so the timestamp can never drift from the
  // day-group heading (the digest rows do the same).
  const timeLabel = formatCalendar(metadata.occurred_at || created_at);

  return (
    <div className="flex items-stretch gap-6 bg-white p-6 hover:bg-alpha-40">
      <Link
        to={url || buildMissedCallUrl(metadata)}
        onClick={handleClick}
        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-4"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <HugeiconsIcon
            icon={Call02Icon}
            size={16}
            className="text-danger-500 shrink-0"
            aria-hidden
          />
          <p className="truncate text-sm font-medium text-danger-500">
            {missedCallTitle(metadata)}
          </p>
        </div>

        <div className="flex items-center gap-4 min-w-0">
          <span
            className={cx(
              "size-3 rounded-full shrink-0",
              read_at ? "bg-alpha-40" : "bg-primary-500"
            )}
          />
          <Avatar name={callerName} size={44} className="font-bold shrink-0" />
          <div className="min-w-0">
            <p className="text-base font-bold text-content-1000 truncate">
              {callerName}
            </p>
            <p className="text-sm text-content-400 truncate">
              Called and no one answered
            </p>
          </div>
        </div>
      </Link>

      <div className="flex shrink-0 flex-col items-end justify-between gap-4">
        <p className="text-content-300 font-medium text-sm">{timeLabel}</p>
        <CallBackButton metadata={metadata} />
      </div>
    </div>
  );
};
